import { createContext, useContext, memo } from "react"
import { useFormContext } from "@/components/forms/form"

interface SectionProps {
    children: React.ReactNode
    className?: string
    design?: string
    hoverEffect?: boolean
    highlightable?: boolean
    highlight?: boolean
    onClick?: () => void
    padX?: boolean
    title?: string
}

export const SectionPadContext = createContext(true)
export const useSectionPad = () => useContext(SectionPadContext)

const Section: React.FC<SectionProps> = ({ children, className, design = ``, highlight, highlightable, onClick, padX = true, title }) => {
    const withinForm = useFormContext().isLoading !== undefined
    const frameClass = withinForm && !highlightable ? "" : "border-[1px] border-gray-400"

    const getDesignClasses = () => {
        switch (design) {
            case "card":
                return `p-4 rounded-md ${frameClass}`;
            case "panel":
                return "rounded-md relative bg-white backdrop-blur-sm shadow-xl p-4 mx-2 lg:mx-0";
            case "card-centered":
                return `mx-2 lg:mx-0 lg:p-6 rounded-md relative transition-all ${frameClass} items-center justify-center gap-y-4 text-center`;
            case "spaced":
                return "lg:p-2 rounded-md relative transition-all";
            case "card-grid-large":
                return "relative !grid lg:grid-cols-2 gap-x-4 gap-y-4";
            case "card-grid-3":
                return "relative !grid grid-cols-1 lg:grid-cols-3 gap-x-4 gap-y-4";
            case "centered-x":
                return "flex flex-col items-center mx-2";
            default:
                return padX ? "px-1 lg:px-0" : "px-0";
        }
    };

    return (
        <SectionPadContext.Provider value={padX}>
        <div
            className={`
                ${getDesignClasses()}
                ${highlight ? '!border-blue-500 !border-1 bg-blue-100' : ''}
                flex
                flex-col
                gap-y-2
                py-2
                ${highlightable ? 'cursor-pointer hover:border-blue-400 hover:border-1 hover:bg-blue-100' : ''}
                ${className}
            `}
            onClick={onClick}
        >
            {title && (
                <div className="flex w-full items-center gap-x-3">
                    <div className="h-px min-w-4 flex-1 bg-gray-300" />
                    <span className="shrink-0 text-center text-sm font-medium text-gray-700">{title}</span>
                    <div className="h-px min-w-4 flex-1 bg-gray-300" />
                </div>
            )}
            {children}
        </div>
        </SectionPadContext.Provider>
    )
}

export default memo(Section)
